import { t, type MessageKey } from "../i18n/t";
import type { FindingStatus, Role } from "../api/client";

const TZ = "Europe/Oslo";
const dateTimeFmt = new Intl.DateTimeFormat("nb-NO", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: TZ,
});
const dateFmt = new Intl.DateTimeFormat("nb-NO", { dateStyle: "long", timeZone: TZ });
const numberFmt = new Intl.NumberFormat("nb-NO");
const inputPartsFmt = new Intl.DateTimeFormat("en-GB", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZone: TZ,
});

function parse(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateTime(value: string | null | undefined): string {
  const d = parse(value);
  return d ? dateTimeFmt.format(d) : t("common.dash");
}

export function formatDate(value: string | null | undefined): string {
  const d = parse(value);
  return d ? dateFmt.format(d) : t("common.dash");
}

/**
 * An ISO timestamp as a `<input type="datetime-local">` value in Norwegian time
 * (`YYYY-MM-DDTHH:mm`). The backend reads naive form values as Europe/Oslo, so this round-trips.
 */
export function toDateTimeInput(value: string | null | undefined): string {
  const d = parse(value);
  if (!d) return "";
  const p = Object.fromEntries(inputPartsFmt.formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

export function formatNumber(value: number | null | undefined): string {
  return value === null || value === undefined ? t("common.dash") : numberFmt.format(value);
}

export function formatSpeed(value: number | null | undefined): string {
  return value === null || value === undefined
    ? t("common.dash")
    : t("common.kmh", { value: formatNumber(value) });
}

export function formatMeters(value: number | null | undefined): string {
  return value === null || value === undefined
    ? t("common.dash")
    : t("common.meters", { value: formatNumber(Math.round(value)) });
}

const SEVERITY_KEYS: Record<string, MessageKey> = {
  high: "severity.high",
  medium: "severity.medium",
  low: "severity.low",
};

export function severityLabel(severity: string): string {
  const key = SEVERITY_KEYS[severity];
  return key ? t(key) : severity;
}

const STATUS_KEYS: Record<FindingStatus, MessageKey> = {
  ny: "status.ny",
  planlagt: "status.planlagt",
  utfort: "status.utfort",
  ikke_relevant: "status.ikke_relevant",
};

export function statusLabel(status: FindingStatus): string {
  return t(STATUS_KEYS[status]);
}

export function isClosedStatus(status: string): boolean {
  return status === "utfort" || status === "ikke_relevant";
}

export function roleLabel(role: Role | string): string {
  if (role === "owner") return t("roles.owner");
  if (role === "member") return t("roles.member");
  return role;
}

const LEVEL_KEYS: Record<string, MessageKey> = {
  barnetrinn: "levels.barnetrinn",
  ungdomstrinn: "levels.ungdomstrinn",
};

export function levelLabel(level: string): string {
  const key = LEVEL_KEYS[level];
  return key ? t(key) : level;
}

const RUN_STATUS_KEYS: Record<string, MessageKey> = {
  queued: "admin.runStatusQueued",
  running: "admin.runStatusRunning",
  succeeded: "admin.runStatusSucceeded",
  failed: "admin.runStatusFailed",
};

export function runStatusLabel(status: string): string {
  const key = RUN_STATUS_KEYS[status];
  return key ? t(key) : status;
}

/** Read the (untyped) `run.summary` dict into a one-line Norwegian summary, or null. */
export function runSummaryText(summary: Record<string, unknown> | null | undefined): string | null {
  if (!summary || typeof summary.findings !== "number") return null;
  const sev =
    typeof summary.by_severity === "object" && summary.by_severity !== null
      ? (summary.by_severity as Record<string, unknown>)
      : {};
  const n = (k: string) => formatNumber(typeof sev[k] === "number" ? sev[k] : 0);
  return t("admin.runSummary", {
    findings: formatNumber(summary.findings),
    high: n("high"),
    medium: n("medium"),
    low: n("low"),
  });
}
